/**
 * SendToFinanceDialog — Demand/KAM-owned capture step that promotes a
 * DRAFT dispatch lot into PENDING_FINANCE while committing all
 * commercial + logistics detail up front.
 *
 * Fires GET /api/dispatch-lots-v2/{lot_id}/prefill-context on open
 * (and again on branch change) to hydrate addresses, transporters,
 * live stock, and prefilled rates in a single call. On submit calls
 * POST /api/dispatch-lots-v2/{lot_id}/send-to-finance which writes
 * dispatch_header onto the lot, creates a Playfield draft dispatch,
 * and reserves stock — see backend routes for the full contract.
 *
 * Point-in-time stock gate (AC4): submit is hard-blocked when any
 * SKU line's committed quantity > live current_stock at source_branch.
 * The RYG advisory strip up top is informational only.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Plus, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StockItemSearch from "@/components/StockItemSearch";
import useAuthStore from "@/store/authStore";

const API = process.env.REACT_APP_BACKEND_URL;

const DOT_TONE = {
  GREEN: "bg-emerald-500",
  YELLOW: "bg-amber-500",
  RED: "bg-rose-500",
};

const emptyAddress = () => ({
  address_id: "",
  label: "",
  gstin: "",
  contact_name: "",
  contact_phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
});

// Feb 2026 — freight/transportation are captured as service-charge rows
// inside the SKU Lines list (AC1). We reuse Dispatch.js's exact
// sentinels + StockItemSearch includeFreight=true widget so the picker
// is visually identical. Sentinels resolve to their own Zoho catalog
// item on the finance side.
const FREIGHT_ITEM_IDS = { FREIGHT: "FREIGHT", TRANSPORTATION_COST: "TRANSPORTATION_COST" };
const FREIGHT_LABELS = {
  FREIGHT: "Freight (service charge)",
  TRANSPORTATION_COST: "Transportation Cost (service charge)",
};
const isFreightItemId = (id) => id === "FREIGHT" || id === "TRANSPORTATION_COST";

// Match the INR formatter used on Dispatch.js's totals + the just-
// shipped LotSubmittedViewerDialog so the whole flow renders identical
// currency strings (₹1,240.00).
const _INR = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR",
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const fmtINR = (n) => _INR.format(Number(n || 0));

/** Read-only display panel for a Bill-To / Ship-To address.
 *  Feb 2026 — the previous freely-editable inputs violated AC4;
 *  editing buyer addresses belongs to the Buyer Master screen, not
 *  this capture dialog. When the buyer has:
 *   • zero addresses of the type → an amber "no address on file"
 *     notice replaces the panel body (AC5).
 *   • one address → shown read-only, no picker (AC1).
 *   • more than one → a Select above the panel lets the user swap
 *     which one to use for this dispatch; body always re-renders
 *     read-only from the picked snapshot (AC3, AC4).
 */
function AddressPanel({ label, value, testIdPrefix, addressList, onPickExisting, typeLabel }) {
  const hasList = (addressList || []).length > 0;
  // A "meaningful" value is one that actually points at a stored
  // address (has an address_id) OR carries any resolved lines. This
  // guards the empty-state — we don't want to hide the empty-state
  // banner just because the parent seeded an emptyAddress() shell.
  const hasResolvedValue = !!(value && (value.address_id || value.line1 || value.city || value.gstin));
  const showEmptyState = !hasList && !hasResolvedValue;

  return (
    <div className="border rounded-md p-3 bg-white space-y-2" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-bold uppercase tracking-wider">{label}</Label>
        {(addressList?.length || 0) > 1 && (
          <Select
            value={value?.address_id || ""}
            onValueChange={(v) => onPickExisting(v)}
          >
            <SelectTrigger className="w-[240px] h-7 text-xs" data-testid={`${testIdPrefix}-picker`}>
              <SelectValue placeholder="Select saved address" />
            </SelectTrigger>
            <SelectContent>
              {addressList.map((a) => (
                <SelectItem key={a.address_id} value={a.address_id} data-testid={`${testIdPrefix}-picker-opt-${a.address_id}`}>
                  {a.label || "(no label)"} {a.is_default ? "· default" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {showEmptyState ? (
        <div
          className="border border-amber-300 bg-amber-50 rounded px-3 py-2 text-[11px] text-amber-900 space-y-0.5"
          data-testid={`${testIdPrefix}-empty`}
        >
          <div className="font-semibold uppercase tracking-wider text-[10px]">
            No {typeLabel} address on file
          </div>
          <div>
            This buyer has no {typeLabel} address saved. Add one from
            the Buyer Master screen and reopen this dialog.
          </div>
        </div>
      ) : (
        <div className="text-xs space-y-1 text-zinc-800" data-testid={`${testIdPrefix}-readonly`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-zinc-900" data-testid={`${testIdPrefix}-label-ro`}>
              {value?.label || "(no label)"}
            </span>
            {addressList?.find((a) => a.address_id === value?.address_id)?.is_default && (
              <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">
                default
              </span>
            )}
            {value?.gstin && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700" data-testid={`${testIdPrefix}-gstin-ro`}>
                GSTIN {value.gstin}
              </span>
            )}
          </div>
          {(value?.contact_name || value?.contact_phone) && (
            <div className="text-[11px] text-zinc-600" data-testid={`${testIdPrefix}-contact-ro`}>
              {[value?.contact_name, value?.contact_phone].filter(Boolean).join(" · ")}
            </div>
          )}
          {value?.line1 && <div data-testid={`${testIdPrefix}-line1-ro`}>{value.line1}</div>}
          {value?.line2 && <div data-testid={`${testIdPrefix}-line2-ro`}>{value.line2}</div>}
          {(value?.city || value?.state || value?.pincode) && (
            <div data-testid={`${testIdPrefix}-cityline-ro`}>
              {[value?.city, [value?.state, value?.pincode].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
            </div>
          )}
          {value?.country && <div className="text-[11px] text-zinc-600" data-testid={`${testIdPrefix}-country-ro`}>{value.country}</div>}
        </div>
      )}
    </div>
  );
}

export default function SendToFinanceDialog({ open, onOpenChange, lot, branches, onSent }) {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ctx, setCtx] = useState(null);

  const [customerPo, setCustomerPo] = useState("");
  const [sourceBranch, setSourceBranch] = useState("");
  const [billTo, setBillTo] = useState(emptyAddress());
  const [shipTo, setShipTo] = useState(emptyAddress());
  const [transporterId, setTransporterId] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [skuLines, setSkuLines] = useState([]);

  const resetForm = () => {
    setCtx(null);
    setCustomerPo("");
    setSourceBranch("");
    setBillTo(emptyAddress());
    setShipTo(emptyAddress());
    setTransporterId("");
    setVehicleNumber("");
    setNotes("");
    setDiscountType("percentage");
    setDiscountValue(0);
    setSkuLines([]);
  };

  // Fetch prefill on open + on source_branch change. Aug 2026 — also
  // refetches when SKU lines are added via the picker so newly-added
  // rows' RATE is populated from customer-specific price_master
  // (the picker's own `prefilled_rate` is a generic list price and
  // the RATE input is now read-only — see AC in the write-up).
  useEffect(() => {
    if (!open || !lot?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (sourceBranch) qs.set("source_branch", sourceBranch);
        // Include picker-added SKU ids so the backend resolves their
        // customer-specific price_master too.
        const lotIds = new Set((lot.lines || []).map((ln) => ln.buyer_sku_id));
        const extras = skuLines
          .map((l) => l.buyer_sku_id)
          .filter((sid) => sid && !lotIds.has(sid) && !isFreightItemId(sid));
        if (extras.length) qs.set("extra_sku_ids", extras.join(","));
        const res = await axios.get(
          `${API}/api/dispatch-lots-v2/${lot.id}/prefill-context${qs.toString() ? `?${qs}` : ""}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        setCtx(res.data);
        // Seed defaults on first load only (source_branch not yet chosen).
        if (!sourceBranch) {
          const bt = (res.data.buyer_addresses?.bill_to || [])[0];
          const st = (res.data.buyer_addresses?.ship_to || [])[0];
          if (bt) setBillTo({ ...emptyAddress(), ...bt });
          if (st) setShipTo({ ...emptyAddress(), ...st });
          // Hydrate line rows from the lot's own lines. RATE is
          // deliberately kept as `null` (not `?? 0`) when no
          // customer-specific price_master entry exists — the UI
          // renders a "no price" badge and blocks submit in that
          // state. The old `?? 0` fallback was what let a user type
          // 2300 into a rendered `0` and think they'd overridden the
          // price (backend silently ignored it — bad UX).
          setSkuLines((lot.lines || []).map((ln) => ({
            buyer_sku_id: ln.buyer_sku_id,
            sku_name: ln.sku_name || ln.buyer_sku_id,
            quantity: Number(ln.quantity || 0),
            rate: res.data.line_rates?.[ln.buyer_sku_id]?.rate ?? null,
            hsn_code: res.data.line_rates?.[ln.buyer_sku_id]?.hsn_code || "",
            gst_rate: res.data.line_rates?.[ln.buyer_sku_id]?.gst_rate ?? 0,
          })));
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load dialog context");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // `skuLines` is intentionally NOT a dep — we only refetch on
    // open/branch change; picker adds trigger a targeted refetch via
    // `refreshPickerRates` below.
  }, [open, lot?.id, sourceBranch, token]);

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  // AC4 — point-in-time stock gate (advisory in-UI + submit-blocking).
  // Only real SKU lines gate stock — freight/transportation rows carry
  // no stock and are excluded.
  const shortages = useMemo(() => {
    if (!ctx || !sourceBranch) return [];
    return skuLines
      .filter((l) => !isFreightItemId(l.buyer_sku_id))
      .map((l) => {
        const avail = ctx.line_stock?.[l.buyer_sku_id] ?? 0;
        return { ...l, avail, shortage: Number(l.quantity || 0) - avail };
      })
      .filter((l) => l.shortage > 0);
  }, [skuLines, ctx, sourceBranch]);

  const canSubmit = !!(
    customerPo.trim() &&
    sourceBranch &&
    // Feb 2026 — Transporter + Vehicle Number relaxed to either/or:
    // one is enough (backend enforces the same rule at write time).
    (transporterId || vehicleNumber.trim()) &&
    skuLines.some((l) => !isFreightItemId(l.buyer_sku_id)) &&
    shortages.length === 0 &&
    // Aug 2026 — hard-block Send-to-Finance when any real SKU line
    // lacks a customer-specific price_master entry. Backend already
    // 400s with PRICE_MASTER_MISSING; frontend now surfaces it before
    // the user can click submit, matching the read-only RATE UI.
    skuLines
      .filter((l) => !isFreightItemId(l.buyer_sku_id))
      .every((l) => l.rate !== null && l.rate !== "" && Number(l.rate) > 0)
  );

  // Aug 2026 — targeted refetch of `line_rates` after the picker adds
  // a real SKU. Passes the newly-added SKU via `extra_sku_ids` so the
  // backend runs its customer-specific price_master lookup for it.
  // Freight/service rows are not included (they have no price_master
  // entry by design).
  const refreshPickerRates = async (newSkuId) => {
    if (!sourceBranch || isFreightItemId(newSkuId)) return;
    try {
      const qs = new URLSearchParams();
      qs.set("source_branch", sourceBranch);
      const lotIds = new Set((lot.lines || []).map((ln) => ln.buyer_sku_id));
      const extras = [
        ...skuLines.map((l) => l.buyer_sku_id),
        newSkuId,
      ].filter((sid) => sid && !lotIds.has(sid) && !isFreightItemId(sid));
      if (extras.length) qs.set("extra_sku_ids", [...new Set(extras)].join(","));
      const res = await axios.get(
        `${API}/api/dispatch-lots-v2/${lot.id}/prefill-context?${qs}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCtx(res.data);
      setSkuLines((prev) => prev.map((l) => {
        if (isFreightItemId(l.buyer_sku_id)) return l;
        const rr = res.data.line_rates?.[l.buyer_sku_id];
        if (!rr) return l;
        return {
          ...l,
          rate: rr.rate ?? null,
          hsn_code: rr.hsn_code || l.hsn_code || "",
          gst_rate: rr.gst_rate ?? l.gst_rate ?? 0,
        };
      }));
    } catch {
      // Non-fatal — user still sees the row; RATE will show `no price`
      // and submit stays disabled until an admin adds the price.
    }
  };

  const handlePickAddress = (which, id) => {
    if (!ctx) return;
    const list = which === "bill_to"
      ? (ctx.buyer_addresses?.bill_to || [])
      : (ctx.buyer_addresses?.ship_to || []);
    const picked = list.find((a) => a.address_id === id);
    if (picked) {
      const setter = which === "bill_to" ? setBillTo : setShipTo;
      setter({ ...emptyAddress(), ...picked });
    }
  };

  const removeSkuLine = (idx) => setSkuLines((prev) => prev.filter((_, i) => i !== idx));

  // AC3 — inline picker toggle. The actual search widget is the
  // shared StockItemSearch component (see JSX below) so we don't
  // rebuild the typeahead machinery here. It handles debouncing,
  // stale-response guarding, and freight-sentinel injection.
  const [skuPickerOpen, setSkuPickerOpen] = useState(false);

  const addSkuLine = (item) => {
    // Freight/Transportation service-charge rows — AC1-4. Injected by
    // the SKU search widget (includeFreight=true) with a sentinel
    // item_id. Qty locked to 1, no stock check, no HSN, no GST.
    if (isFreightItemId(item.item_id)) {
      setSkuLines((prev) => {
        if (prev.some((l) => l.buyer_sku_id === item.item_id)) return prev; // no dupes
        return [
          ...prev,
          {
            buyer_sku_id: item.item_id,
            sku_name: FREIGHT_LABELS[item.item_id] || item.name || item.item_id,
            quantity: 1,
            rate: "",
            hsn_code: "",
            gst_rate: 0,
            is_freight: item.item_id === FREIGHT_ITEM_IDS.FREIGHT,
            is_transportation_cost: item.item_id === FREIGHT_ITEM_IDS.TRANSPORTATION_COST,
          },
        ];
      });
      setSkuPickerOpen(false);
      return;
    }
    // De-dupe — if the SKU is already on the row list, just bump its
    // qty by 1 so the user sees the effect without a phantom row.
    let picked_id = item.item_id;
    setSkuLines((prev) => {
      const idx = prev.findIndex((l) => l.buyer_sku_id === item.item_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Number(next[idx].quantity || 0) + 1 };
        return next;
      }
      return [
        ...prev,
        {
          buyer_sku_id: item.item_id,
          sku_name: item.item_name || item.item_id,
          quantity: 1,
          // Aug 2026 — DO NOT seed the picker's `prefilled_rate`. That
          // value comes from `stock-search` which cascades to a
          // non-customer-specific list price when no customer_saleprice
          // exists — the exact "misleading rate" case the read-only UI
          // is meant to prevent. Leave as `null`; `refreshPickerRates`
          // below will populate the customer-specific rate (or leave
          // `null` and render "no price" if there isn't one).
          rate: null,
          hsn_code: item.hsn_code || "",
          gst_rate: 0,
          is_freight: false,
          is_transportation_cost: false,
        },
      ];
    });
    // Bubble the new SKU into ctx.line_stock so the availability
    // badge for the row lights up immediately without a full refetch.
    setCtx((prev) => (
      prev ? {
        ...prev,
        line_stock: {
          ...(prev.line_stock || {}),
          [item.item_id]: Number(item.current_stock || 0),
        },
      } : prev
    ));
    setSkuPickerOpen(false);
    // Aug 2026 — kick off a customer-specific price refresh so the
    // newly-added row's read-only RATE cell displays either the real
    // price_master value or the "no price" indicator. Runs after
    // setSkuLines has committed via microtask ordering.
    if (picked_id) {
      void refreshPickerRates(picked_id);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Split UI rows: real SKUs → payload.lines (stock-gated,
      // Playfield-draft-committing); freight sentinels → freight_lines
      // (persisted on dispatch_header.freight_lines, no stock, no
      // Playfield draft entry). Matches Dispatch.js's is_freight /
      // is_transportation_cost row shape.
      const skuOnly = skuLines.filter((l) => !isFreightItemId(l.buyer_sku_id));
      const freightOnly = skuLines.filter((l) => isFreightItemId(l.buyer_sku_id));
      const payload = {
        customer_po: customerPo.trim(),
        source_branch: sourceBranch,
        bill_to: billTo,
        ship_to: shipTo,
        // Feb 2026 — either/or: only send the field that's set, null
        // when empty. Backend accepts a null transporter or a blank
        // vehicle_number so long as at least one of the two is set.
        transporter: transporterId ? { vendor_id: transporterId } : null,
        vehicle_number: vehicleNumber.trim() || null,
        freight_lines: freightOnly.map((l) => ({
          item_id: l.buyer_sku_id,
          description: l.sku_name || FREIGHT_LABELS[l.buyer_sku_id] || "",
          quantity: 1,
          rate: l.rate === "" || l.rate == null ? 0 : Number(l.rate),
        })),
        discount: { type: discountType, value: Number(discountValue || 0) },
        notes: notes.trim(),
        lines: skuOnly.map((l) => ({
          buyer_sku_id: l.buyer_sku_id,
          quantity: Number(l.quantity || 0),
        })),
      };
      const res = await axios.post(
        `${API}/api/dispatch-lots-v2/${lot.id}/send-to-finance`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Sent to finance");
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail.insufficient_items) {
        toast.error(`Insufficient stock — ${detail.insufficient_items.length} line(s) short`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to send to finance");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const avail = ctx?.availability_signal;

  // Oct 2026 (AC2) — explicit blocking banner naming the SKUs missing
  // a customer-specific Price Master entry. `canSubmit` already turns
  // the button off when any real SKU row has `rate==null`, but the
  // user needs a direct cue + the fix path, not just a disabled
  // button. Freight sentinel rows are not Price-Master-backed by
  // design (same exclusion the backend applies).
  const unpricedSkuRows = useMemo(
    () => skuLines.filter(
      (l) => !isFreightItemId(l.buyer_sku_id) &&
             (l.rate === null || l.rate === "" || !(Number(l.rate) > 0)),
    ),
    [skuLines],
  );

  // Live totals mirroring the just-shipped LotSubmittedViewerDialog +
  // Dispatch.js's dispatch-totals block. Sub Total spans SKU-only rows;
  // freight rows contribute to Freight Total instead. Discount applies
  // to the item sub-total only (freight billed at cost, undiscounted).
  const { subTotal, discountAmount, freightTotal, grandTotal } = useMemo(() => {
    const sub = skuLines
      .filter((l) => !isFreightItemId(l.buyer_sku_id))
      .reduce((acc, l) => acc + Number(l.quantity || 0) * Number(l.rate || 0), 0);
    const dv = Number(discountValue || 0);
    const dAmt = discountType === "amount" ? dv : (sub * dv / 100);
    const fr = skuLines
      .filter((l) => isFreightItemId(l.buyer_sku_id))
      .reduce((acc, l) => acc + Number(l.rate || 0), 0); // qty locked at 1
    return {
      subTotal: sub,
      discountAmount: dAmt,
      freightTotal: fr,
      grandTotal: sub - dAmt + fr,
    };
  }, [skuLines, discountType, discountValue]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="send-to-finance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-bold uppercase">
            <Send className="h-5 w-5" />
            Send to Finance — {lot?.lot_number}
          </DialogTitle>
        </DialogHeader>

        {/* Advisory RYG strip (AC4 — advisory only, not a submit gate) */}
        {avail && (
          <div
            className="border rounded-md px-3 py-2 flex items-center gap-3 bg-zinc-50"
            data-testid="s2f-availability-strip"
          >
            <span className={`inline-block w-3 h-3 rounded-full ${DOT_TONE[avail.status] || "bg-zinc-300"}`} />
            <span className="text-xs uppercase tracking-wider font-semibold">{avail.status}</span>
            {avail.target_date && (
              <span className="text-xs text-zinc-600">Target: <span className="font-mono">{avail.target_date}</span></span>
            )}
            <span className="text-[10px] text-zinc-500 ml-auto">Advisory — not a submit gate.</span>
          </div>
        )}

        {/* Oct 2026 (AC2) — blocking banner for missing Price Master. */}
        {unpricedSkuRows.length > 0 && (
          <div
            className="border border-rose-300 bg-rose-50 text-rose-900 rounded-md px-3 py-2 text-xs"
            data-testid="s2f-price-master-block"
          >
            <div className="font-semibold uppercase tracking-wide">
              Cannot send to finance — Price Master entry missing
            </div>
            <div className="mt-1">
              No active price_master entry for buyer{" "}
              <span className="font-mono">{lot?.customer_name || ""}</span> ×{" "}
              {unpricedSkuRows.map((l) => (
                <span key={l.buyer_sku_id} className="font-mono mr-1">
                  {l.buyer_sku_id}
                </span>
              ))}
              . Add a customer-specific price under Price Master and reopen this dialog.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold uppercase">Customer PO *</Label>
            <Input
              value={customerPo}
              onChange={(e) => setCustomerPo(e.target.value)}
              placeholder='Enter PO number (use "NA" if none)'
              data-testid="s2f-customer-po"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase">Source Branch *</Label>
            <Select value={sourceBranch} onValueChange={setSourceBranch}>
              <SelectTrigger data-testid="s2f-source-branch">
                <SelectValue placeholder="Select branch (stock will be locked here)" />
              </SelectTrigger>
              <SelectContent>
                {(branches || []).map((b) => (
                  <SelectItem key={b.branch_id || b.name} value={b.name || b.branch_id} data-testid={`s2f-source-branch-opt-${b.branch_id || b.name}`}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <AddressPanel
            label="Bill To *"
            value={billTo}
            typeLabel="BILL_TO"
            testIdPrefix="s2f-billto"
            addressList={ctx?.buyer_addresses?.bill_to}
            onPickExisting={(id) => handlePickAddress("bill_to", id)}
          />
          <AddressPanel
            label="Ship To *"
            value={shipTo}
            typeLabel="SHIP_TO"
            testIdPrefix="s2f-shipto"
            addressList={ctx?.buyer_addresses?.ship_to}
            onPickExisting={(id) => handlePickAddress("ship_to", id)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold uppercase">Transporter</Label>
            {(ctx?.transporters || []).length === 0 ? (
              <div className="border rounded-md px-3 py-2 text-xs text-amber-800 bg-amber-50 flex items-center gap-2" data-testid="s2f-transporter-empty">
                <AlertTriangle className="w-4 h-4" />
                Transporter vendor type not yet configured. Ops onboarding pending.
              </div>
            ) : (
              <Select value={transporterId} onValueChange={setTransporterId}>
                <SelectTrigger data-testid="s2f-transporter">
                  <SelectValue placeholder="Select transporter" />
                </SelectTrigger>
                <SelectContent>
                  {ctx.transporters.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`s2f-transporter-opt-${t.id}`}>
                      {t.name}{t.gst ? ` · ${t.gst}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase">Vehicle Number</Label>
            <Input
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
              placeholder="e.g. MH12AB1234"
              data-testid="s2f-vehicle-number"
            />
          </div>
        </div>
        {/* Feb 2026 — either/or hint. Renders only when both fields are
            empty so the operator sees exactly what the submit rule is. */}
        {!transporterId && !vehicleNumber.trim() && (
          <div className="text-[11px] text-amber-700 italic -mt-1" data-testid="s2f-transporter-vehicle-hint">
            Provide at least one of Transporter or Vehicle Number.
          </div>
        )}

        <Separator />

        {/* SKU + freight service-charge lines. Freight rows share the
            row shape and picker widget with real SKUs (via
            StockItemSearch includeFreight=true, matching Dispatch.js). */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs font-bold uppercase">SKU Lines</Label>
            <span className="text-[10px] text-zinc-500">Stock shown for the selected source branch. Add “Freight” or “Transportation Cost” from the same picker.</span>
          </div>
          <div className="border rounded-md">
            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider bg-zinc-50 px-2 py-1 font-semibold text-zinc-600">
              <div className="col-span-4">SKU</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Available</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-1">HSN</div>
              <div className="col-span-1"></div>
            </div>
            {skuLines.length === 0 && (
              <div className="text-xs text-zinc-500 px-2 py-3 text-center">No SKU lines on this lot.</div>
            )}
            {skuLines.map((l, idx) => {
              const isFreight = isFreightItemId(l.buyer_sku_id);
              const stock = ctx?.line_stock?.[l.buyer_sku_id] ?? null;
              const shortage = !isFreight && sourceBranch && stock !== null && Number(l.quantity || 0) > stock;
              return (
                <div key={l.buyer_sku_id + "_" + idx} className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 border-t text-xs" data-testid={`s2f-sku-row-${idx}`}>
                  <div className="col-span-4 font-mono truncate" title={l.buyer_sku_id}>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium">{l.sku_name}</span>
                      {isFreight && (
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`s2f-sku-freight-tag-${idx}`}>
                          service
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">{l.buyer_sku_id}</div>
                  </div>
                  <div className="col-span-2 text-right">
                    <Input
                      className="no-spinner h-7 text-right"
                      type="number" min="1"
                      value={isFreight ? 1 : l.quantity}
                      readOnly={isFreight}
                      disabled={isFreight}
                      onChange={(e) => {
                        if (isFreight) return;
                        const q = e.target.value;
                        setSkuLines((prev) => prev.map((r, i) => i === idx ? { ...r, quantity: q } : r));
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      data-testid={`s2f-sku-qty-${idx}`}
                    />
                  </div>
                  <div className="col-span-2 text-right">
                    {isFreight ? (
                      <span className="text-[10px] text-zinc-400">—</span>
                    ) : sourceBranch ? (
                      <Badge className={shortage ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"} data-testid={`s2f-sku-avail-${idx}`}>
                        {stock === null ? "—" : stock}
                      </Badge>
                    ) : <span className="text-[10px] text-zinc-400">select branch</span>}
                  </div>
                  <div className="col-span-2 text-right">
                    {isFreight ? (
                      // Freight / Transportation are service charges,
                      // NOT SKUs sold to the customer — they have no
                      // price_master entry by design. Keep editable.
                      <Input
                        className="no-spinner h-7 text-right"
                        type="number" min="0" step="0.01"
                        placeholder="optional"
                        value={l.rate ?? ""}
                        onChange={(e) => setSkuLines((prev) => prev.map((r, i) => i === idx ? { ...r, rate: e.target.value } : r))}
                        onWheel={(e) => e.currentTarget.blur()}
                        data-testid={`s2f-sku-rate-${idx}`}
                      />
                    ) : (l.rate === null || l.rate === "" || Number(l.rate) <= 0) ? (
                      // Aug 2026 — read-only "no price" indicator. This
                      // is the state that would trip the backend
                      // PRICE_MASTER_MISSING 400 at submit time — surface
                      // it up front so the operator can go add the price
                      // to price_master before clicking submit (which
                      // `canSubmit` blocks anyway).
                      <div
                        className="h-7 flex items-center justify-end gap-1 px-2 rounded border border-rose-200 bg-rose-50 text-rose-700"
                        title="No customer-specific price found in Price Master. Add one for this Buyer × SKU pair and reopen the dialog."
                        data-testid={`s2f-sku-rate-noprice-${idx}`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        <span className="text-[10px] font-medium uppercase tracking-wider">
                          No price
                        </span>
                      </div>
                    ) : (
                      // Aug 2026 — RATE is read-only. Sourced from
                      // customer-specific `price_master` via
                      // `/prefill-context` → `line_rates`. The backend
                      // (`services.dispatch_price_resolver`) is already
                      // authoritative and IGNORES any client-supplied
                      // rate at submit; making the input non-editable
                      // stops the "I typed 2300 and it looked
                      // accepted" UX bug the user reported.
                      <div
                        className="h-7 flex items-center justify-end gap-1 pr-2 rounded border border-zinc-200 bg-zinc-50 text-zinc-700 font-mono tabular-nums"
                        title="Rate is sourced from Price Master (customer-specific). Read-only."
                        data-testid={`s2f-sku-rate-${idx}`}
                      >
                        <span className="text-[10px] uppercase tracking-wider text-emerald-700">PM</span>
                        <span className="text-xs">{fmtINR(l.rate)}</span>
                      </div>
                    )}
                  </div>
                  <div className="col-span-1 text-[11px] font-mono">{isFreight ? "—" : (l.hsn_code || "—")}</div>
                  <div className="col-span-1 text-right">
                    <Button size="icon" variant="ghost" onClick={() => removeSkuLine(idx)} data-testid={`s2f-sku-remove-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          {/* AC3 — add another SKU line (or a Freight / Transportation
              Cost service-charge row). Uses the same StockItemSearch
              includeFreight=true widget as Dispatch.js so the picker
              behaves identically. Disabled until a source_branch is
              chosen so the underlying /stock-search call has a branch
              to key off. */}
          <div className="mt-2">
            {!skuPickerOpen ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSkuPickerOpen(true)}
                disabled={!sourceBranch}
                data-testid="s2f-add-sku-line"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add SKU / Freight line{!sourceBranch ? " (pick source branch first)" : ""}
              </Button>
            ) : (
              <div className="border rounded-md p-2 bg-zinc-50 space-y-1" data-testid="s2f-sku-picker">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <StockItemSearch
                      branch={sourceBranch}
                      type="FG"
                      includeFreight={true}
                      excludeIds={skuLines.map((l) => l.buyer_sku_id)}
                      placeholder='Search Buyer SKU or type "Freight" / "Transportation" (min 2 chars)…'
                      onSelect={(picked) => addSkuLine({
                        item_id: picked.item_id,
                        item_name: picked.name,
                        current_stock: picked.current_stock,
                        prefilled_rate: picked.prefilled_rate,
                        hsn_code: picked.hsn_code,
                      })}
                      testId="s2f-sku-picker-input"
                    />
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setSkuPickerOpen(false)} data-testid="s2f-sku-picker-close">
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          {shortages.length > 0 && (
            <div className="mt-2 border border-rose-300 bg-rose-50 rounded-md px-3 py-2 text-xs text-rose-900" data-testid="s2f-shortage-banner">
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="w-3.5 h-3.5" /> Insufficient stock at {sourceBranch}:
              </div>
              <ul className="mt-1 pl-5 list-disc">
                {shortages.map((s) => (
                  <li key={s.buyer_sku_id}>{s.sku_name} — need {s.quantity}, have {s.avail} (short {s.shortage})</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Separator />

        {/* Notes + right-justified compact Totals panel (AC5 — matches
            Dispatch.js's `dispatch-totals` block and the just-shipped
            LotSubmittedViewerDialog `viewer-totals-block`). Discount
            type + value sit inline inside the totals grid, not in a
            wide left-aligned block. Freight is undiscounted (billed at
            cost); GST is finance-owned and never shown here (AC4). */}
        <div className="grid grid-cols-2 gap-3 items-start">
          <div>
            <Label className="text-xs font-bold uppercase">Notes</Label>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes (optional)" data-testid="s2f-notes" />
          </div>
          <div className="border rounded-lg bg-zinc-50/40 p-4 self-start" data-testid="s2f-totals">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-3 items-center text-sm">
              <div className="text-zinc-600 font-medium">Sub Total</div>
              <div></div>
              <div className="font-mono font-semibold text-right" data-testid="s2f-sub-total">
                {fmtINR(subTotal)}
              </div>

              <div className="text-zinc-600 font-medium">Discount</div>
              <div className="flex gap-1.5">
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="s2f-discount-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">%</SelectItem>
                    <SelectItem value="amount">₹</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="0"
                  className="no-spinner h-8 w-[90px] text-right text-xs"
                  data-testid="s2f-discount-value"
                />
              </div>
              <div className="font-mono text-right text-rose-700" data-testid="s2f-discount-amount">
                −{fmtINR(discountAmount)}
              </div>

              <div className="text-zinc-600 font-medium">Freight</div>
              <div className="text-[11px] text-zinc-500 text-right">at cost</div>
              <div className="font-mono font-semibold text-right" data-testid="s2f-freight-total">
                {fmtINR(freightTotal)}
              </div>

              <div className="col-span-3 border-t mt-1 pt-3 grid grid-cols-[1fr_auto] items-center">
                <div className="font-semibold text-zinc-800">Grand Total</div>
                <div className="font-mono text-lg font-bold text-orange-700" data-testid="s2f-grand-total">
                  {fmtINR(grandTotal)}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 leading-snug">
              Discount applies to item lines only; freight billed undiscounted.
              GST is added by Finance at invoice creation.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="s2f-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || submitting || loading}
            data-testid="s2f-submit"
          >
            {submitting ? "Sending…" : "Send to Finance"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
